import { CaretDownFilled, CloseCircleOutlined } from '@ant-design/icons';
import { THEME } from '@common/constants/theme.constant';
import { FieldV2, IFieldV2Props } from '@common/general-ui/field-v2/field-v2.component';
import { Icon } from '@common/general-ui/icon/icon.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { useScreenMediaQuery } from '@common/hooks/use-screen-media-query.hook';
import { useTimeout } from '@common/hooks/use-timeout.hook';
import { IDictionary } from '@common/interfaces/dictionary.interface';
import { css as emotionCSS } from '@emotion/css';
import { Global, SerializedStyles } from '@emotion/react';
import { Select as AntdSelect, SelectProps } from 'antd';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from 'twin.macro';
const { Option } = AntdSelect;

export const SelectV2Option = Option;
export type ISelectV2ChangeHandler = (e: { value: string; name: string }) => void;

export interface ISelectV2Props<TName = string, TOption = unknown> extends IFieldV2Props<string, TName> {
    options?: ISelectV2Option<TOption>[];
    onChange?: ISelectV2ChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    className?: string;
    renderSelectOption?: (data: ISelectV2Option<TOption>) => ReactNode;
    optionLabelProp?: string;
    onSearch?: (data: string) => void;
    showSearch?: boolean;
    allowClear?: boolean;
    allowCustomValue?: boolean;
    onClear?: () => void;
    dropdownRender?: SelectProps<TName>['dropdownRender'];
    filterOption?: SelectProps<TName>['filterOption'];
    searchValue?: SelectProps<TName>['searchValue'];
}

export interface ISelectV2Option<T = unknown> {
    label: ReactNode;
    value: string | number;
    isVisible?: boolean;
    disabled?: boolean;
    data?: T;
}

// TODO bug in select - placeholder not visible second time (after focus, check e2e , investigate how fix)
export function SelectV2<T = unknown>(props: ISelectV2Props<string, T>): JSX.Element {
    const {
        onChange,
        onBlur,
        onSearch,
        onClear,
        allowCustomValue,
        options = [],
        optionLabelProp,
        defaultValue,
        value,
        name,
        showSearch = false,
        dropdownRender,
        filterOption,
        searchValue,
    } = props;
    const [internalOptions, setInternalOptions] = useState<ISelectV2Option<T>[]>(options || []);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { isDownMD } = useScreenMediaQuery();

    useEffect(() => {
        if (options !== undefined) {
            setInternalOptions(options);
        }
    }, [options]);

    const handleChange = useCallback(
        (value: string) => {
            onChange && onChange({ name, value });
        },
        [onChange, name],
    );

    const { start: startSearch, clear: clearSearchCallback } = useTimeout<string>((str) => {
        onSearch && onSearch(str);
    }, 100);
    const refIsBlur = useRef<boolean>();
    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Antd before blur event - always clear search value - no possible disable this feature
            clearSearchCallback();

            onBlur && onBlur(e);
            setTimeout(() => {
                refIsBlur.current = false;
            }, 100);
        },
        [onBlur, clearSearchCallback],
    );

    const handleFocus = useCallback(() => {
        // consoleService.log('focus');
    }, []);

    const handleSearch = useCallback(
        (str: string) => {
            startSearch(str);
            if (allowCustomValue) {
                setInternalOptions([
                    {
                        label: str,
                        value: str,
                        data: str as unknown as T,
                        isVisible: true,
                    },
                    ...(options || []),
                ]);
            }
        },
        [options, allowCustomValue, startSearch],
    );

    const handleDropDownVisibilityChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (!open) {
                return;
            }

            if (allowCustomValue) {
                setInternalOptions([...options]);
            }
        },
        [allowCustomValue, options],
    );

    // block scrolling on mobile when dropdown is open contains options
    useEffect(() => {
        if (!isDownMD) {
            return;
        }

        // prevent body from scrolling when dropdown is open and contains options
        if (isOpen && internalOptions.length > 0) {
            document.body.style.height = '100%';
            document.body.style.overflowY = 'hidden';
        } else {
            // remove anti-scroll styles
            document.body.style.removeProperty('height');
            document.body.style.removeProperty('overflow-y');
        }
    }, [isOpen, internalOptions, isDownMD]);

    const optionsCache = useMemo(() => {
        const res = (props?.options || []).reduce((acc: IDictionary, el: ISelectV2Option) => {
            acc[el.value] = el.label;
            return acc;
        }, {});
        return res;
    }, [props.options]);

    const handleFilter = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (input: string, option: any): boolean => {
            return optionsCache[option.value]?.toLowerCase().indexOf(input.toLowerCase()) >= 0 || option.value == input;
        },
        [optionsCache],
    );
    return (
        <FieldV2 {...props} tw="relative">
            <Global styles={styles.dropDownBox} />
            <div tw="relative">
                {props.readOnly && <Readonly />}
                <AntdSelect
                    // {...removeUndefined({ onSearch: props.readOnly && !showSearch ? undefined : handleSearch })}
                    showArrow={true}
                    css={styles.root(!!props.readOnly, !!props.errorMessage)}
                    allowClear={props.allowClear}
                    tabIndex={props.readOnly ? -1 : 0}
                    dropdownClassName={styles.dropDown}
                    dropdownStyle={{ width: '50px'}}
                    showSearch={!props.readOnly && showSearch}
                    style={{ width: '100%' }}
                    optionLabelProp={optionLabelProp}
                    placeholder={props.placeholder}
                    suffixIcon={<CaretDownFilled />}
                    onDropdownVisibleChange={handleDropDownVisibilityChange}
                    defaultValue={defaultValue}
                    value={value || undefined}
                    optionFilterProp="children"
                    onSearch={allowCustomValue || showSearch ? handleSearch : undefined}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    dropdownRender={(prev): JSX.Element =>
                        dropdownRender ? dropdownRender(prev) : props.readOnly ? <></> : prev
                    }
                    searchValue={searchValue}
                    filterOption={filterOption !== undefined ? filterOption : handleFilter}
                    clearIcon={
                        <div tw="mt-[-1px]" onClick={onClear}>
                            <Icon tw="w-4 h-4 text-black" icon={CloseCircleOutlined} />
                        </div>
                    }
                    virtual={false}
                >
                    {internalOptions.map((o: ISelectV2Option<T>): JSX.Element => {
                        return props.renderSelectOption ? (
                            <React.Fragment key={o.value}>{props.renderSelectOption(o)}</React.Fragment>
                        ) : (
                            <Option
                                data-parent-testid={'field-' + props.name}
                                key={o.value}
                                value={o.value}
                                disabled={o.disabled}
                            >
                                {o.label}
                            </Option>
                        );
                    })}
                </AntdSelect>
            </div>
        </FieldV2>
    );
}

const styles = {
    root: (readOnly: boolean, error: boolean): (SerializedStyles | boolean)[] => [
        css`
            display: block;
            && .ant-select-selector {
                height: 48px;
                border: 1px solid ${THEME.GRAY_300};
                line-height: 24px;
                border-radius: 4px;
                padding: 12px;
                display: flex;
                align-items: center;
                width: 100%;
                overflow: hidden;
                :hover {
                    border-color: ${THEME.TEXT_BLACK};
                }
                && .rc-virtual-list-holder: {
                    maxHeight: 'none !important',
                    overflow: 'visible !important',
                },
            }
            &.ant-select-focused {
                && .ant-select-selector {
                    border: 1px solid ${THEME.TEXT_BLACK};
                    outline-color: transparent;
                    outline-style: none;
                    box-shadow: none;
                }

                &&.ant-select-open {
                    && .ant-select-arrow .anticon {
                        transform: rotate(180deg);
                    }
                }
            }
            && .ant-select-selection-item {
                font-weight: 300;
                text-overflow: ellipsis;
            }

            && .ant-select-selection-search {
                display: flex;
                align-items: center;
                padding: 12px 0;
            }
            && .ant-select-selection-search-input {
                font-weight: 300;
            }

            && .ant-select-arrow {
                height: 16px;
                width: 16px;
                color: ${THEME.TEXT_BLACK};
                position: absolute;
                top: 45%;
                svg {
                    height: 16px;
                    width: 16px;
                }
            }

            .ant-select-open {
                background: red;
                && .ant-select-arrow .anticon {
                    transform: rotate(180deg);
                }
            }
        `,
        readOnly &&
            css`
                && .ant-select-selector,
                && .ant-select-selector:hover {
                    border: 1px solid ${THEME.READONLY_COLOR};
                }
                &.ant-select-focused {
                    && .ant-select-selection-search-input,
                    && .ant-select-selection-item {
                        color: ${THEME.BLACK};
                    }
                    && .ant-select-selector {
                        border: 1px solid ${THEME.READONLY_COLOR};
                        outline-color: transparent;
                        outline-style: none;
                        box-shadow: none;
                    }
                }
            `,
        error &&
            css`
                && .ant-select-selector {
                    border-color: ${THEME.PINK};
                }
            `,
    ],
    dropDown: emotionCSS`
            && .ant-select-item-option {
                font-weight: 300;
                padding: 8px 12px;
                &:not(:last-child) { 
                    border-bottom: 1px solid ${THEME.GRAY_300};
                }
            }
            && .ant-select-item-option-selected {
                display: flex;
                justify-content: space-between;
                position: relative;
                background-color: ${THEME.WHITE};
                color: black;
            }
            && .ant-select-item-option-selected::after {
                content: "";
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='64 64 896 896' focusable='false' data-icon='check' width='1em' height='1em' fill='currentColor' aria-hidden='true'%3E%3Cpath d='M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 00-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z'%3E%3C/path%3E%3C/svg%3E");
                background-repeat: no-repeat;
                background-position: center right;
                min-width: 15px;
                margin-left: 5px;
                height: 16px;
                align-self: center;
            }
            && .ant-select-item-option-active {
                background-color: ${THEME.GRAY_200};
            }
    `,
    dropDownBox: css`
        && .ant-select-dropdown {
            border-radius: 4px;
            border: 1px solid ${THEME.GRAY_300};
            padding: 0;
            margin-top: 6px;
        }
        && .ant-select-dropdown-empty {
            border: none;
        }
    `,
};
